import { useState } from 'react';
import { drawerOfPart, frontOfPart, movePanelToAssembly, shelfOfPart, type EvaluatedProject, type PanelPart, type ProjectDocument } from '@scs/domain-engine';
import { panelKeyOfPart } from './AssemblyProperties';
import type { EditorStore, Selection } from './store';
import { itemSpan } from '@scs/domain-engine';
import { ROOM_ITEM_ICON } from './RoomProperties';
import { itemLabel, lineLabel } from './roomLabels';

/** Inline name editor: Enter / blur saves, Esc cancels. */
function RenameInput({ value, onDone }: { value: string; onDone(v: string | null): void }) {
  const [text, setText] = useState(value);
  return (
    <input
      className="input tree-rename"
      autoFocus
      value={text}
      maxLength={200}
      onFocus={(e) => e.target.select()}
      onChange={(e) => setText(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') onDone(text.trim() || null);
        if (e.key === 'Escape') onDone(null);
      }}
      onBlur={() => onDone(text.trim() || null)}
    />
  );
}

/**
 * Scene outliner: units and (for the selected one) their panels. Double-click (or F2) renames an
 * assembly, cabinet or panel; panels of an assembly can be dragged onto another assembly.
 */
export function SceneTree({ doc, ev, selection, store, canEdit }: { doc: ProjectDocument; ev: EvaluatedProject; selection: Selection; store: EditorStore; canEdit: boolean }) {
  const [renaming, setRenaming] = useState<string | null>(null);
  const [drag, setDrag] = useState<{ unitId: string; key: string } | null>(null);
  const [dropOn, setDropOn] = useState<string | null>(null);
  const [roomOpen, setRoomOpen] = useState(true);
  const plan = doc.roomPlan;
  const walls = plan?.lines.filter((l) => l.kind === 'wall') ?? [];

  const renameUnit = (id: string, name: string | null) => {
    setRenaming(null);
    if (!name) return;
    store.exec('Preimenovanje', (d) => {
      const u = [...d.cabinets, ...d.assemblies].find((x) => x.id === id);
      if (u && u.name !== name) u.name = name;
    });
  };
  const renamePanel = (unitId: string, key: string, label: string | null) => {
    setRenaming(null);
    if (!label) return;
    store.exec('Naziv ploče', (d) => {
      const p = d.assemblies.find((a) => a.id === unitId)?.panels.find((x) => x.key === key);
      if (p && p.label !== label) p.label = label;
    });
  };
  const drop = (toId: string) => {
    const src = drag;
    setDrag(null);
    setDropOn(null);
    if (!src || src.unitId === toId) return;
    let newKey: string | null = null;
    store.exec('Premještanje ploče u sklop', (d) => {
      newKey = movePanelToAssembly(d, src.unitId, src.key, toId);
    });
    if (newKey) store.select({ cabinetIds: [toId], partId: `${toId}/${newKey}` });
  };

  return (
    <ul className="tree">
      {[...doc.cabinets, ...doc.assemblies].map((c) => {
        const sel = selection.cabinetIds.includes(c.id);
        const evc = ev.cabinets.find((e) => e.instance.id === c.id);
        const errs = ev.issues.filter((i) => i.cabinetId === c.id && i.severity === 'error').length;
        const isAsm = 'panels' in c;
        const canDrop = isAsm && !!drag && drag.unitId !== c.id && !c.locked;
        const partRow = (p: PanelPart) => {
          const key = isAsm ? panelKeyOfPart(c, p.localKey) : undefined;
          const editable = canEdit && !c.locked && !!key;
          const rid = `${c.id}/${p.localKey}`;
          return (
            <li key={p.id}>
              <button
                className={[selection.partId === p.id || selection.extraPartIds?.includes(p.id) ? 'sel' : '', drag?.unitId === c.id && drag.key === key ? 'dragging' : ''].join(' ')}
                onClick={(e) => {
                  if (!(e.ctrlKey || e.metaKey)) return store.select({ cabinetIds: [c.id], partId: p.id });
                  // Ctrl+click: add/remove the part (e.g. boundaries for shelves, panels to link).
                  const all = [selection.partId, ...(selection.extraPartIds ?? [])].filter((x): x is string => !!x);
                  const next = all.includes(p.id) ? all.filter((x) => x !== p.id) : [...all, p.id];
                  store.select({
                    cabinetIds: [...new Set([...selection.cabinetIds, c.id])],
                    partId: next[0] ?? null,
                    extraPartIds: next.slice(1),
                  });
                }}
                onDoubleClick={() => editable && setRenaming(rid)}
                onKeyDown={(e) => e.key === 'F2' && editable && (e.stopPropagation(), setRenaming(rid))}
                draggable={editable && renaming !== rid}
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', p.label);
                  e.dataTransfer.effectAllowed = 'move';
                  setDrag({ unitId: c.id, key: key! });
                }}
                onDragEnd={() => {
                  setDrag(null);
                  setDropOn(null);
                }}
                title={editable ? 'Dvoklik ili F2 = preimenuj · prevuci na drugi sklop' : undefined}
              >
                {renaming === rid && isAsm ? <RenameInput value={c.panels.find((x) => x.key === key)!.label} onDone={(v) => renamePanel(c.id, key!, v)} /> : p.label}
                <span className="flag mono">{p.localKey}</span>
              </button>
            </li>
          );
        };
        return (
          <li key={c.id}>
            <button
              className={[sel && !selection.partId ? 'sel' : sel ? 'has-sel' : '', dropOn === c.id ? 'drop' : ''].join(' ')}
              onClick={(e) =>
                store.select({
                  cabinetIds: e.shiftKey ? (sel ? selection.cabinetIds.filter((x) => x !== c.id) : [...selection.cabinetIds, c.id]) : [c.id],
                  partId: null,
                })
              }
              onDoubleClick={() => canEdit && !c.locked && setRenaming(c.id)}
              onKeyDown={(e) => e.key === 'F2' && canEdit && !c.locked && (e.stopPropagation(), setRenaming(c.id))}
              title={canEdit ? 'Dvoklik ili F2 = preimenuj' : undefined}
              onDragOver={(e) => {
                if (!canDrop) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                setDropOn(c.id);
              }}
              onDragLeave={() => dropOn === c.id && setDropOn(null)}
              onDrop={(e) => {
                e.preventDefault();
                if (canDrop) drop(c.id);
              }}
            >
              {errs > 0 && <span className="sev error" title={`${errs} greška`} />}
              {renaming === c.id ? <RenameInput value={c.name} onDone={(v) => renameUnit(c.id, v)} /> : <span style={{ opacity: c.hidden ? 0.5 : 1 }}>{c.name}</span>}
              <span className="flag">
                {c.preset ? '★' : ''}
                {isAsm ? '▦' : ''}
                {c.locked ? '🔒' : ''}
                {c.hidden ? '◌' : ''}
                {c.transform.mirrored ? '⇋' : ''}
              </span>
            </button>
            {sel && selection.cabinetIds.length === 1 && evc && (
              <ul className="tree sub">
                {evc.result.parts.filter((p) => !(isAsm && (shelfOfPart(c, p.localKey) || frontOfPart(c, p.localKey) || drawerOfPart(c, p.localKey)))).map(partRow)}
                {isAsm &&
                  (c.shelfSets ?? []).map((set) => {
                    const rid = `set:${c.id}/${set.id}`;
                    const active = set.count - set.deleted.filter((i) => i <= set.count).length;
                    return (
                      <li key={rid}>
                        <button
                          className={`shelf-row ${selection.shelfSetId === set.id && !selection.partId ? 'sel' : ''}`}
                          onClick={() =>
                            store.select({
                              cabinetIds: [c.id],
                              partId: null,
                              shelfSetId: set.id,
                            })
                          }
                          onDoubleClick={() => canEdit && !c.locked && setRenaming(rid)}
                          title="Set polica — klik bira sve police (svojstva, vrati obrisane, link)"
                        >
                          {renaming === rid ? (
                            <RenameInput
                              value={set.label}
                              onDone={(v) => {
                                setRenaming(null);
                                if (v)
                                  store.exec('Naziv polica', (d) => {
                                    const x = d.assemblies.find((a) => a.id === c.id)?.shelfSets?.find((y) => y.id === set.id);
                                    if (x) x.label = v;
                                  });
                              }}
                            />
                          ) : (
                            <>▤ {set.label}</>
                          )}
                          <span className="flag mono">
                            {active}/{set.count}
                          </span>
                        </button>
                        <ul className="tree sub shelf-children">{evc.result.parts.filter((p) => p.localKey.startsWith(`${set.id}-`)).map(partRow)}</ul>
                      </li>
                    );
                  })}
                {isAsm &&
                  (c.drawerSets ?? []).map((set) => (
                    <li key={`drawers:${set.id}`}>
                      <button
                        className={`shelf-row drawer-row ${selection.drawerSetId === set.id && !selection.partId ? 'sel' : ''}`}
                        onClick={() => store.select({ cabinetIds: [c.id], partId: null, drawerSetId: set.id })}
                        title="Ladice — klik bira sve ladice (svojstva)"
                      >
                        ▥ {set.label}
                        <span className="flag mono">{set.count}</span>
                      </button>
                      <ul className="tree sub shelf-children">{evc.result.parts.filter((p) => drawerOfPart(c, p.localKey)?.set.id === set.id).map(partRow)}</ul>
                    </li>
                  ))}
                {isAsm && (c.fronts ?? []).length > 0 && (
                  <li key="fronts">
                    <button
                      className={`group-row ${selection.frontGroup && !selection.partId ? 'sel' : ''}`}
                      onClick={() => store.select({ cabinetIds: [c.id], partId: null, frontGroup: true })}
                      title="Grupa fronti — fuga između fronti"
                    >
                      ▭ Fronte
                      <span className="flag mono">{c.fronts!.length}</span>
                    </button>
                    <ul className="tree sub shelf-children">{evc.result.parts.filter((p) => frontOfPart(c, p.localKey)).map(partRow)}</ul>
                  </li>
                )}
              </ul>
            )}
          </li>
        );
      })}
      {plan && walls.length > 0 && (
        <li className="tree-room">
          <button className={`group-row ${selection.room ? 'has-sel' : ''}`} onClick={() => setRoomOpen(!roomOpen)} title="Zidovi prostorije — klik na zid bira ga (i u 3D)">
            <span className="tree-caret">{roomOpen ? '▾' : '▸'}</span> Prostorija
            <span className="flag mono">{walls.length}</span>
          </button>
          {roomOpen && (
            <ul className="tree sub">
              {walls.map((w) => {
                const items = plan.items.filter((i) => i.lineId === w.id).sort((a, b) => (itemSpan(plan, a)?.u0 ?? 0) - (itemSpan(plan, b)?.u0 ?? 0));
                const wallSel = selection.room?.lineId === w.id;
                return (
                  <li key={w.id}>
                    <button className={`wall-row ${wallSel && !selection.room?.itemId ? 'sel' : wallSel ? 'has-sel' : ''}`} onClick={() => store.select({ cabinetIds: [], partId: null, room: { lineId: w.id } })}>
                      ▮ {lineLabel(plan, w.id)}
                      {items.length > 0 && <span className="flag mono">{items.length}</span>}
                    </button>
                    {items.length > 0 && (
                      <ul className="tree sub shelf-children">
                        {items.map((i) => (
                          <li key={i.id}>
                            <button className={selection.room?.itemId === i.id ? 'sel' : ''} onClick={() => store.select({ cabinetIds: [], partId: null, room: { lineId: w.id, itemId: i.id } })}>
                              <span aria-hidden>{ROOM_ITEM_ICON[i.kind]}</span> {itemLabel(plan, i)}
                              <span className="flag mono">
                                {Math.round(i.width / 1000)}×{Math.round(i.height / 1000)}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </li>
      )}
      {drag && doc.assemblies.length < 2 && (
        <li className="faint" style={{ padding: '6px 12px', fontSize: 11 }}>
          Nema drugog sklopa na koji se ploča može prevući.
        </li>
      )}
      {doc.cabinets.length + doc.assemblies.length === 0 && (
        <li className="faint" style={{ padding: '8px 12px' }}>
          Prazna scena — otvorite Katalog ili dodajte ploču.
        </li>
      )}
    </ul>
  );
}
